import qs from 'qs';
import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import { FormRegisterEmailData } from 'dashboard/forms/FormRegisterEmail';
import { FormRegisterFullData } from 'dashboard/forms/FormRegisterFull';
import { FormRegisterTrialEssentialData } from 'dashboard/forms/FormRegisterTrialEssential';
import SessionClientOnboard from 'dashboard/lib/clients/SessionClientOnboard';
import useRedirectIfLoggedIn from 'dashboard/lib/hooks/useRedirectIfLoggedIn';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { OnboardingFlowV1 } from 'dashboard/models/onboarding-flow-v1';
import { OrganizationPlanKey } from 'dashboard/models/organization-plan';
import { showMessage } from 'dashboard/redux/actions/messages';
import { saveOnboardingFlow } from 'dashboard/redux/actions/onboarding-flow';
import { RootState } from 'dashboard/redux/store';
import DashboardRegisterView from 'dashboard/views/DashboardRegisterView';
import { SegmentClient } from 'lib/clients/SegmentClient';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiModel } from 'lib/interfaces/api';

export const DashboardRegisterRoute = (props: PropsFromRedux) => {
  useRedirectIfLoggedIn();

  const navigate = useNavigate();
  const location = useLocation();
  const [savedEmail, setSavedEmail] = useState('');
  const [emailStepCompleted, setEmailStepCompleted] = useState(false);
  const [fullFormStepCompleted, setFullFormStepCompleted] = useState(false);

  const parsedQuery = qs.parse(location.search, { ignoreQueryPrefix: true });
  const source = parsedQuery.source ?? '';
  const shard = parsedQuery.shard ?? '';
  const version = 'v2-single-page'; // used to differentiate between /signup and /register experiments

  const createOnboardingFlow = async ({
    'email-address': email,
    'recaptcha-token': recaptchaToken,
  }: FormRegisterEmailData) => {
    // NB: We prioritize UTM params from the current URL, otherwise fall back to localStorage
    const currentUrl = new URL(window.location.href);
    const urlParams = new URL(currentUrl).searchParams;
    const gclid =
      urlParams.get('gclid') || StorageClient.getLocalItem(LocalStorageKey.MarketingGclid);
    const utmSourceLocalStorage = StorageClient.getLocalItem(LocalStorageKey.MarketingUtmSource);
    const utmSource =
      urlParams.get('utm_source') ?? urlParams.get('ref') ?? utmSourceLocalStorage ?? '';
    const utmMedium =
      urlParams.get('utm_medium') || StorageClient.getLocalItem(LocalStorageKey.MarketingUtmMedium);
    const utmCampaign =
      urlParams.get('utm_campaign') ||
      StorageClient.getLocalItem(LocalStorageKey.MarketingUtmCampaign);
    const utmTerm =
      urlParams.get('utm_term') || StorageClient.getLocalItem(LocalStorageKey.MarketingUtmTerm);
    const utmContent =
      urlParams.get('utm_content') ||
      StorageClient.getLocalItem(LocalStorageKey.MarketingUtmContent);
    const utmAudience =
      urlParams.get('utm_audience') ||
      StorageClient.getLocalItem(LocalStorageKey.MarketingUtmAudience);
    const utmOther =
      urlParams.get('utm_other') || StorageClient.getLocalItem(LocalStorageKey.MarketingUtmOther);

    try {
      const onboardingFlow = await SessionClientOnboard.postResourceCreate<OnboardingFlowV1>(
        {
          type: ApiModel.OnboardingFlowV1,
          attributes: {
            'email-address': email,
            source: source as string,
            gclid,
            'utm-source': utmSource,
            'utm-medium': utmMedium,
            'utm-campaign': utmCampaign,
            'utm-term': utmTerm,
            'utm-content': utmContent,
            'utm-audience': utmAudience,
            'utm-other': utmOther,
            version: version,
            'recaptcha-token': recaptchaToken,
          },
        },
        {
          meta: { shard },
        },
      );

      SegmentClient.sendSegmentIdentifyEvent({
        firstname: onboardingFlow.data.attributes['name-first'],
        lastname: onboardingFlow.data.attributes['name-last'],
        email: onboardingFlow.data.attributes['email-address'],
      });

      props.saveOnboardingFlow(onboardingFlow.data);

      setSavedEmail(onboardingFlow.data.attributes?.['email-address'] ?? '');
      setEmailStepCompleted(true);
    } catch (err) {
      setEmailStepCompleted(false);

      if (err instanceof ApiResponseError && err.status === 409) {
        props.showMessage(
          'Sorry, this email address is already taken. Please try with a different email.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('An error occurred. Please try again.', MessageType.Failure);
      }
    }
  };

  const updateOnboardingFlow = async ({
    password,
    'name-first': nameFirst,
    'name-last': nameLast,
    'organization-name': organizationName,
    'organization-domain': organizationDomain,
    'organization-industry': organizationIndustry,
    'attribution-sources': attributionSources,
  }: FormRegisterFullData) => {
    try {
      const updatedOnboardingFlow = await SessionClientOnboard.updateResource<OnboardingFlowV1>({
        type: ApiModel.OnboardingFlowV1,
        attributes: {
          password: password,
          'name-first': nameFirst,
          'name-last': nameLast,
          'organization-name': organizationName,
          'organization-domain': organizationDomain,
          'organization-industry': organizationIndustry,
          'attribution-sources': attributionSources,
        },
        id: props.onboardingFlow.id,
      });

      props.saveOnboardingFlow(updatedOnboardingFlow.data);

      setFullFormStepCompleted(true);
    } catch (err) {
      setFullFormStepCompleted(false);
      props.showMessage('An error occurred. Please try again.', MessageType.Failure);
    }
  };

  const updateOnboardingFlowWithTrialEssentialInfo = async ({
    'business-identification-number': businessIdentificationNumber,
    'business-country-code': businessCountryCode,
    'use-case-description': useCaseDescription,
  }: FormRegisterTrialEssentialData) => {
    try {
      const updatedOnboardingFlow = await SessionClientOnboard.updateResource<OnboardingFlowV1>({
        type: ApiModel.OnboardingFlowV1,
        attributes: {
          // We have to collect these two fields independently b/c they require separate components
          'business-identification-number': businessIdentificationNumber,
          'business-identification-number-type':
            businessCountryCode === 'US' ? 'employer_identification_number' : 'other',
          'business-country-code': businessCountryCode,
          'requested-plan-key': OrganizationPlanKey.TrialEssential,
          'use-case-description': useCaseDescription,
        },
        id: props.onboardingFlow.id,
      });

      props.saveOnboardingFlow(updatedOnboardingFlow.data);
      navigate(`/dashboard/register/confirm`);
    } catch (err) {
      if (
        err instanceof ApiResponseError &&
        err.status === 422 &&
        err.errors?.[0] &&
        typeof err.errors[0].details === 'string'
      ) {
        props.showMessage(err.errors[0].details, MessageType.Failure);
      } else {
        props.showMessage('An error occurred. Please try again.', MessageType.Failure);
      }
    }
  };

  const skipTrialEssential = async () => {
    navigate(`/dashboard/register/confirm`);
  };

  return (
    <DashboardRegisterView
      savedEmail={savedEmail}
      emailStepCompleted={emailStepCompleted}
      fullFormStepCompleted={fullFormStepCompleted}
      createOnboardingFlow={createOnboardingFlow}
      updateOnboardingFlow={updateOnboardingFlow}
      updateOnboardingFlowWithTrialEssentialInfo={updateOnboardingFlowWithTrialEssentialInfo}
      skipTrialEssential={skipTrialEssential}
      setEmailStepCompleted={setEmailStepCompleted}
      setFullFormStepCompleted={setFullFormStepCompleted}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    onboardingFlow: state.onboardingFlow.onboardingFlow,
  };
};

const mapDispatchToProps = {
  saveOnboardingFlow,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardRegisterRoute);
